import {useAtom, useAtomValue} from 'jotai';
import {useEffect, useMemo} from 'react';
import {useInView} from 'react-intersection-observer';
import {useParams} from 'react-router-dom';
import {Button} from '@/components/Button';
import {LoadingBar} from '@/components/LoadingBar';
import {
  SidePanelBase, SidePanelHead, SidePanelHeadTitleSection, SidePanelHeadTitleText,
  SidePanelScrollBody
} from '@/components/SidePanel/StyledComponents';
import {Typography} from '@/components/Typography';
import {useKodataStoreInfiniteQuery, useQueryRegionByPoint} from '@/map/api';
import {kodataStoresFiltersAtom, kodataStoresSortAtom, mapCenterAtom} from '@/map/atom';
import {MAP_QUERY_DEBOUNCE_MS} from '@/map/constants';
import {GeoDataType, KodataStoresQueryFilters} from '@/map/enums';
import {convertKodataStoresFilterFormat} from '@/map/utils';
import {FlexCenter, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {FindStoreOne} from './Detail';
import {Filter} from './Filter';
import {TopSalesStoreListItem} from './Item';
import {SortBar} from './Sort';

const REGION = [
  {key: KodataStoresQueryFilters.sido, propertyKey: 'sido'},
  {key: KodataStoresQueryFilters.sgg, propertyKey: 'sgg'},
  {key: KodataStoresQueryFilters.emd, propertyKey: 'emd'},
];

export const FindStores = () => {
  const params = useParams<{
    id?: string;
  }>();
  const mapCenter = useAtomValue(mapCenterAtom);
  const {data: regionPointData} = useQueryRegionByPoint(
    !!mapCenter && {lng: mapCenter.x, lat: mapCenter.y, type: GeoDataType.Emd},
    MAP_QUERY_DEBOUNCE_MS
  );

  const region = useMemo<typeof filter>(() => {
    if (!regionPointData) {
      return [];
    }

    return REGION.map(({key, propertyKey}) => ({
      key,
      value: (regionPointData as any)[propertyKey],
    }));
  }, [regionPointData]);

  const [filter, setFilter] = useAtom(kodataStoresFiltersAtom);
  const {sort} = useAtomValue(kodataStoresSortAtom);

  const enabled = !!region?.length;
  const searchFilter =
    enabled && convertKodataStoresFilterFormat(filter.concat(region));

  const {data, hasNextPage, isFetchingNextPage, isFetching, fetchNextPage} =
    useKodataStoreInfiniteQuery({
      filter: searchFilter || undefined,
      perPage: 30,
      sort,
    });

  const fetchNext = () => fetchNextPage();

  const [moreRef, inView] = useInView();

  const onSearch = (
    nextFilter: Array<{key: KodataStoresQueryFilters; value: any}>
  ) => {
    setFilter(nextFilter);
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const areaString = region ? region.map(({value}) => value).join(' ') : '';
  const totalCount = data?.pages?.[0]?.result?.count ?? 0;

  return (
    <>
      {!!params.id?.length && <FindStoreOne id={params.id} />}
      <SidePanelBase style={{display: params.id ? 'none' : undefined}}>
        <SidePanelHead>
          <SidePanelHeadTitleSection>
            <SidePanelHeadTitleText>매장찾기</SidePanelHeadTitleText>
          </SidePanelHeadTitleSection>
          <Filter
            disabled={!enabled}
            searchFilter={filter}
            area={areaString}
            onSearch={onSearch}
          />
          <SortBar disabled={!enabled} />
        </SidePanelHead>
        {isFetching && !isFetchingNextPage ? (
          <SidePanelScrollBody>
            <PaddingBox spacing={50}>
              <LoadingBar color={palette.gray800} />
            </PaddingBox>
          </SidePanelScrollBody>
        ) : totalCount > 0 ? (
          <SidePanelScrollBody>
            {!!data?.pages?.length &&
              data.pages.map(
                (page) =>
                  !!page?.result?.items?.length &&
                  page.result.items.map((item, idx) => (
                    <TopSalesStoreListItem key={idx} item={item} />
                  ))
              )}
            {hasNextPage && (
              <FlexCenter>
                <Button
                  disabled={isFetching || isFetchingNextPage}
                  size="small"
                  variant="outline"
                  color="black"
                  ref={moreRef}
                  onClick={fetchNext}
                >
                  {isFetchingNextPage ? (
                    <LoadingBar size={20} color={palette.gray800} />
                  ) : (
                    <Typography variant="body1">더 가져오기</Typography>
                  )}
                </Button>
              </FlexCenter>
            )}
          </SidePanelScrollBody>
        ) : (
          <SidePanelScrollBody>
            <PaddingBox spacing={16}>
              <Typography variant="medium14" align="center" as="div">
                {enabled ? (
                  '검색 결과가 없습니다.'
                ) : (
                  <>
                    검색할 수 없는 지역입니다.
                    <br />
                    지도를 움직여 위치를 이동해보세요.
                  </>
                )}
              </Typography>
            </PaddingBox>
          </SidePanelScrollBody>
        )}
      </SidePanelBase>
    </>
  );
};
